import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Users, MessageCircle, TrendingUp, DollarSign, ArrowUp, ArrowDown,
  Sparkles, BarChart3, Target, Package, Activity, Zap,
  FileText, Radio, ChevronRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useApp } from '../contexts/AppContext';
import { cn } from '../lib/utils';
import { useTranslation } from 'react-i18next';
import { InfoTip } from '../components/InfoTip';
import { NoahVoiceButton } from '../components/NoahVoiceButton';
import { StreakCard } from '../components/StreakCard';
import { InfoBubble } from '../components/InfoBubble';

const API = process.env.REACT_APP_BACKEND_URL;

const statusLabels = {
  new: 'Nuevos', lead: 'Leads', contacted: 'Contactados', prospect: 'Prospectos',
  qualified: 'Calificados', negotiation: 'Negociacion', won: 'Ganados', closed_won: 'Ganados',
  lost: 'Perdidos', closed_lost: 'Perdidos', inquiry: 'Consultas'
};
const statusColors = {
  new: '#1a4034', lead: '#002d2c', contacted: '#2d5b45', prospect: '#559975',
  qualified: '#77bd9b', negotiation: '#f4eb9f', won: '#c48a4b', closed_won: '#c48a4b',
  lost: '#ef4444', closed_lost: '#ef4444', inquiry: '#9fd8bb'
};

// ---- Animated number ----
const AnimatedNumber = ({ value, prefix = '', suffix = '', decimals = 0 }) => {
  const [display, setDisplay] = useState(value);
  const prevRef = useRef(value);

  useEffect(() => {
    const from = prevRef.current;
    const to = value;
    if (from === to) return;
    const diff = to - from;
    const steps = 20;
    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(from + diff * eased);
      if (step >= steps) {
        clearInterval(timer);
        setDisplay(to);
        prevRef.current = to;
      }
    }, 25);
    return () => clearInterval(timer);
  }, [value]);

  const formatted = decimals > 0 ? display.toFixed(decimals) : Math.round(display).toLocaleString();
  return <span>{prefix}{formatted}{suffix}</span>;
};

// ---- Mini sparkline ----
const Sparkline = ({ data, color, height = 32 }) => {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const w = 100;
  const points = data.map((v, i) => `${(i / (data.length - 1)) * w},${height - ((v - min) / range) * (height - 4)}`).join(' ');
  return (
    <svg width={w} height={height} className="opacity-60">
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

// ---- Pulse dot ----
const PulseDot = ({ color = 'emerald' }) => (
  <span className="relative flex h-2.5 w-2.5">
    <span className={`animate-ping absolute inline-flex h-full w-full rounded-full bg-${color}-400 opacity-75`} />
    <span className={`relative inline-flex rounded-full h-2.5 w-2.5 bg-${color}-500`} />
  </span>
);

// ---- KPI Card ----
const KpiCard = ({ title, value, prefix, suffix, decimals, icon: Icon, gradient, trend, sparkData, sparkColor, onClick, info, detail }) => {
  const [open, setOpen] = React.useState(false);
  const slug = title.toLowerCase().replace(/\s/g, '-');
  return (
  <div>
  <Card data-testid={`kpi-${slug}`}
    className={cn("overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-lg", open && "ring-1 ring-primary/40")}
    onClick={() => (detail ? setOpen((o) => !o) : onClick?.())}>
    <CardContent className="p-0">
      <div className="p-4 flex items-start justify-between">
        <div>
          <p className="text-xs text-muted-foreground font-medium mb-1 flex items-center gap-1">
            {title}
            {info && <InfoTip {...info} />}
          </p>
          <p className="text-2xl font-bold tracking-tight">
            <AnimatedNumber value={value} prefix={prefix} suffix={suffix} decimals={decimals} />
          </p>
          {trend && (
            <div className={cn("flex items-center gap-1 mt-1 text-xs font-medium", trend > 0 ? "text-emerald-700 dark:text-emerald-400" : "text-red-600 dark:text-red-400")}>
              {trend > 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
              {Math.abs(trend)}%
            </div>
          )}
        </div>
        <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", gradient)}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
      {sparkData && (
        <div className="px-4 pb-3 -mt-1">
          <Sparkline data={sparkData} color={sparkColor || '#559975'} />
        </div>
      )}
    </CardContent>
  </Card>
  {detail && <InfoBubble open={open} title={detail.title} note={detail.note} items={detail.items} cta={detail.cta} testId={`kpi-bubble-${slug}`} />}
  </div>
  );
};

// ---- Pipeline bar ----
const PipelineBar = ({ statusMap, total }) => {
  const stages = ['new', 'lead', 'prospect', 'contacted', 'qualified', 'negotiation', 'closed_won', 'won', 'closed_lost', 'lost', 'inquiry'];
  return (
    <div className="space-y-2">
      <div className="flex h-3 rounded-full overflow-hidden bg-muted/30">
        {stages.map(s => {
          const count = statusMap[s] || 0;
          const pct = total > 0 ? (count / total) * 100 : 0;
          if (pct === 0) return null;
          return (
            <div key={s} className="transition-all duration-700 ease-out"
              style={{ width: `${pct}%`, backgroundColor: statusColors[s] }}
              title={`${statusLabels[s]}: ${count}`} />
          );
        })}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {stages.map(s => {
          const count = statusMap[s] || 0;
          if (count === 0) return null;
          return (
            <div key={s} className="flex items-center gap-1.5 text-xs">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: statusColors[s] }} />
              <span className="text-muted-foreground">{statusLabels[s]}</span>
              <span className="font-semibold">{count}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ---- Main Dashboard ----
const Dashboard = () => {
  const navigate = useNavigate();
  const { customers, chats, campaigns, products } = useApp();
  const { t } = useTranslation();
  const [metrics, setMetrics] = useState(null);
  const [connected, setConnected] = useState(false);
  const [sparkHistory, setSparkHistory] = useState({ customers: [], revenue: [], chats: [], conversion: [] });
  const eventSourceRef = useRef(null);
  const dailySummaryRef = useRef(null);
  const [dailySummary, setDailySummary] = useState(null);

  // Fetch Noah daily summary once
  useEffect(() => {
    if (!dailySummaryRef.current) {
      dailySummaryRef.current = true;
      fetch(`${API}/api/noah/daily-summary`).then(r => r.json()).then(setDailySummary).catch(() => {});
    }
  }, []);

  // SSE connection
  const connectSSE = useCallback(() => {
    if (eventSourceRef.current) eventSourceRef.current.close();
    const es = new EventSource(`${API}/api/metrics/realtime`);
    eventSourceRef.current = es;

    es.onopen = () => setConnected(true);
    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.timestamp) {
          setMetrics(data);
          setSparkHistory(prev => ({
            customers: [...prev.customers.slice(-19), data.customers.total],
            revenue: [...prev.revenue.slice(-19), data.revenue.total],
            chats: [...prev.chats.slice(-19), data.chats.total],
            conversion: [...prev.conversion.slice(-19), data.conversion.rate],
          }));
        }
      } catch (err) { console.warn('SSE parse error:', err?.message); }
    };
    es.onerror = () => { setConnected(false); es.close(); setTimeout(connectSSE, 3000); };
  }, []);

  useEffect(() => {
    connectSSE();
    return () => { if (eventSourceRef.current) eventSourceRef.current.close(); };
  }, [connectSSE]);

  // Fallback from context if SSE hasn't connected yet
  const total = metrics?.customers?.total ?? customers.length;
  const statusMap = metrics?.customers?.by_status ?? {};
  const revenue = metrics?.revenue?.total ?? 0;
  const pipeline = metrics?.revenue?.pipeline ?? 0;
  const avgTicket = metrics?.revenue?.avg_ticket ?? 0;
  const convRate = metrics?.conversion?.rate ?? 0;
  const won = metrics?.conversion?.won ?? 0;
  const totalChats = metrics?.chats?.total ?? chats.length;
  const unread = metrics?.chats?.unread ?? 0;
  const msgToday = metrics?.activity?.messages_today ?? 0;
  const quotesTotal = metrics?.activity?.quotes_total ?? 0;
  const quotesAccepted = metrics?.activity?.quotes_accepted ?? 0;
  const prodCount = metrics?.products ?? products.length;
  const campMetrics = metrics?.campaigns ?? [];

  return (
    <div data-testid="dashboard-page" className="min-h-screen">
      <Header title={t('dashboard.title')} subtitle={t('dashboard.subtitle')} />

      <div className="p-4 sm:p-6 space-y-5 pb-24">
        {/* Live indicator */}
        <div className="flex items-center gap-2">
          <PulseDot color={connected ? 'emerald' : 'red'} />
          <span className="text-xs text-muted-foreground">
            {connected ? t('dashboard.realtime') : t('dashboard.reconnecting')}
          </span>
          <Radio className={cn("w-3 h-3 ml-1", connected ? "text-emerald-700 dark:text-emerald-400" : "text-red-600 dark:text-red-400")} />
          {metrics?.timestamp && (
            <span className="text-[10px] text-muted-foreground ml-auto">
              {new Date(metrics.timestamp).toLocaleTimeString('es')}
            </span>
          )}
        </div>

        <StreakCard />

        {/* KPI Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiCard title={t('dashboard.totalCustomers')} value={total} icon={Users}
            gradient="bg-primary"
            sparkData={sparkHistory.customers} sparkColor="#3b82f6"
            info={{ title: 'Clientes totales', text: 'Todos los contactos de tus campañas. Solo ves los tuyos: el aislamiento por dueño y campaña es estricto.', source: 'Clientes creados a mano o por Noah al entrar un mensaje nuevo', to: '/customers', linkLabel: 'Abrir Clientes' }}
            detail={{
              title: 'Reparto de tus clientes',
              items: [
                { label: 'En el pipeline visual', value: total, to: '/bubbles' },
                { label: 'Ganados', value: (statusMap.closed_won || 0) + (statusMap.won || 0), to: '/deals' },
                { label: 'Perdidos', value: (statusMap.closed_lost || 0) + (statusMap.lost || 0), to: '/deals' },
                { label: 'En negociación', value: statusMap.negotiation || 0, to: '/deals' },
              ],
              note: 'Cada cliente pertenece a una sola campaña: Noah nunca cruza datos.',
              cta: { label: 'Ver listado de clientes', to: '/customers' },
            }}
            onClick={() => navigate('/customers')} />
          <KpiCard title={t('dashboard.revenue')} value={revenue} prefix="$" icon={DollarSign}
            gradient="bg-primary"
            sparkData={sparkHistory.revenue} sparkColor="#10b981"
            info={{ title: 'Ingresos', text: 'Suma del valor de los clientes marcados como Ganado. Se actualiza en cuanto cierras un trato.', source: 'Campo valor de los clientes en etapa Ganado', to: '/deals', linkLabel: 'Ver ventas' }}
            detail={{
              title: 'De dónde salen tus ingresos',
              items: [
                { label: 'Ventas cerradas', value: won, to: '/deals' },
                { label: 'Ticket promedio', value: `$${Number(avgTicket || 0).toLocaleString()}`, to: '/invoices' },
                { label: 'Valor en pipeline', value: `$${Number(pipeline || 0).toLocaleString()}`, to: '/bubbles' },
                { label: 'Pagos protegidos (Escrow)', value: 'Abrir', to: '/escrow' },
              ],
              note: 'Los cobros y retiros se gestionan en el área de pagos.',
              cta: { label: 'Ir al área de pagos e ingresos', to: '/escrow' },
            }} />
          <KpiCard title={t('dashboard.conversations')} value={totalChats} icon={MessageCircle}
            gradient="bg-primary"
            sparkData={sparkHistory.chats} sparkColor="#559975"
            info={{ title: 'Conversaciones', text: 'Chats activos en todos los canales conectados. Noah responde según el modo de cada campaña.', source: 'Inbox unificado (WhatsApp, Messenger, Instagram, SMS, email)', to: '/inbox', linkLabel: 'Abrir Inbox' }}
            detail={{
              title: 'Tus conversaciones',
              items: [
                { label: 'Mensajes de hoy', value: msgToday, to: '/inbox' },
                { label: 'Sin leer', value: unread, to: '/inbox' },
                { label: 'Tickets escalados', value: 'Ver tablero', to: '/tickets' },
                { label: 'Canales conectados', value: 'Revisar', to: '/connections' },
              ],
              note: 'Noah atiende solo las campañas que tengas en modo automático.',
              cta: { label: 'Abrir el Inbox unificado', to: '/inbox' },
            }}
            onClick={() => navigate('/inbox')} />
          <KpiCard title={t('dashboard.conversionRate')} value={convRate} suffix="%" decimals={1} icon={Target}
            gradient="bg-brand-copper"
            sparkData={sparkHistory.conversion} sparkColor="#f97316"
            info={{ title: 'Tasa de conversión', text: 'Qué porcentaje de tus clientes termina en Ganado. Es el indicador que Noah intenta subir cada semana.', source: 'Clientes en Ganado ÷ clientes totales', to: '/reports', linkLabel: 'Ver reportes por campaña' }}
            detail={{
              title: 'Cómo se calcula',
              items: [
                { label: 'Ganados', value: won, to: '/deals' },
                { label: 'Clientes totales', value: total, to: '/customers' },
                { label: 'Comparar campañas', value: 'Abrir', to: '/reports' },
                { label: 'Objeciones que frenan ventas', value: 'Ver', to: '/reports' },
              ],
              note: 'Ganados ÷ clientes totales. Noah detecta las objeciones que bajan este número.',
              cta: { label: 'Ver reportes por campaña', to: '/reports' },
            }} />
        </div>

        {/* Pipeline + Activity Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Pipeline visual */}
          <Card className="lg:col-span-2 glass-card">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-brand-deep dark:text-brand-deep dark:text-brand-green" /> {t('dashboard.pipeline')}
                </h3>
                <Button variant="ghost" size="sm" className="text-xs h-7 gap-1" onClick={() => navigate('/bubbles')}>
                  {t('dashboard.viewKanban')} <ChevronRight className="w-3 h-3" />
                </Button>
              </div>
              <PipelineBar statusMap={statusMap} total={total} />
              <div className="grid grid-cols-3 gap-3 pt-2">
                <div className="text-center p-2 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                  <p className="text-lg font-bold text-emerald-700 dark:text-emerald-400"><AnimatedNumber value={won} /></p>
                  <p className="text-[10px] text-muted-foreground">{t('dashboard.salesCount')}</p>
                </div>
                <div className="text-center p-2 bg-violet-500/10 rounded-lg border border-violet-500/20">
                  <p className="text-lg font-bold text-brand-deep dark:text-brand-deep dark:text-brand-green">$<AnimatedNumber value={pipeline} /></p>
                  <p className="text-[10px] text-muted-foreground">{t('dashboard.inPipeline')}</p>
                </div>
                <div className="text-center p-2 bg-brand-green/10 rounded-lg border border-brand-green/20">
                  <p className="text-lg font-bold text-brand-deep dark:text-brand-green">$<AnimatedNumber value={avgTicket} /></p>
                  <p className="text-[10px] text-muted-foreground">{t('dashboard.avgTicket')}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Activity pulse */}
          <Card className="glass-card">
            <CardContent className="p-4 space-y-3">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <Activity className="w-4 h-4 text-brand-deep dark:text-brand-green" /> {t('dashboard.activityToday')}
              </h3>
              <div className="space-y-2.5">
                <button onClick={() => navigate('/inbox')} data-testid="activity-messages-today"
                  className="w-full flex items-center justify-between p-2.5 rounded-lg bg-primary/10 border border-primary/20 hover:bg-primary/20 transition-colors">
                  <div className="flex items-center gap-2">
                    <MessageCircle className="w-4 h-4 text-brand-deep dark:text-brand-green" />
                    <span className="text-xs">{t('dashboard.messagesToday')}</span>
                  </div>
                  <span className="font-bold text-sm"><AnimatedNumber value={msgToday} /></span>
                </button>
                <button onClick={() => navigate('/invoices')} data-testid="activity-quotes"
                  className="w-full flex items-center justify-between p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/20 hover:bg-amber-500/20 transition-colors">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-amber-700 dark:text-amber-400" />
                    <span className="text-xs">{t('dashboard.quotes')}</span>
                  </div>
                  <span className="font-bold text-sm"><AnimatedNumber value={quotesTotal} /></span>
                </button>
                <button onClick={() => navigate('/invoices')} data-testid="activity-quotes-accepted"
                  className="w-full flex items-center justify-between p-2.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20 hover:bg-emerald-500/20 transition-colors">
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-emerald-700 dark:text-emerald-400" />
                    <span className="text-xs">{t('dashboard.quotesAccepted')}</span>
                  </div>
                  <span className="font-bold text-sm"><AnimatedNumber value={quotesAccepted} /></span>
                </button>
                <button onClick={() => navigate('/inbox')} data-testid="activity-unread"
                  className="w-full flex items-center justify-between p-2.5 rounded-lg bg-brand-green/10 border border-brand-green/20 hover:bg-brand-green/20 transition-colors">
                  <div className="flex items-center gap-2">
                    <MessageCircle className="w-4 h-4 text-brand-deep dark:text-brand-green" />
                    <span className="text-xs">{t('dashboard.unread')}</span>
                  </div>
                  <Badge variant="outline" className={cn("text-xs", unread > 0 ? "text-red-600 dark:text-red-400 border-red-500" : "text-emerald-700 dark:text-emerald-400 border-emerald-500")}>
                    {unread}
                  </Badge>
                </button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Campaigns + Noah row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Campaigns */}
          <Card className="glass-card">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-amber-700 dark:text-amber-400" /> {t('dashboard.campaigns')}
                  <Badge variant="outline" className="text-[10px]">{campMetrics.length || campaigns.length}</Badge>
                </h3>
                <Button variant="ghost" size="sm" className="text-xs h-7 gap-1" onClick={() => navigate('/campaigns')}>
                  {t('dashboard.viewAll')} <ChevronRight className="w-3 h-3" />
                </Button>
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {(campMetrics.length > 0 ? campMetrics : campaigns.map(c => ({
                  id: c.id, name: c.name, color: c.color, customers: 0, won: 0, revenue: 0
                }))).map(camp => (
                  <button key={camp.id} onClick={() => navigate('/campaigns')} data-testid={`dashboard-campaign-${camp.id}`}
                    className="w-full text-left flex items-center gap-3 p-2.5 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition-colors">
                    <div className="w-2.5 h-8 rounded-full" style={{ backgroundColor: camp.color || '#888' }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate">{camp.name}</p>
                      <p className="text-[10px] text-muted-foreground">{camp.customers} {t('dashboard.customers')}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs font-bold text-emerald-700 dark:text-emerald-400">{camp.won} {t('dashboard.sales')}</p>
                      <p className="text-[10px] text-muted-foreground">${camp.revenue?.toLocaleString()}</p>
                    </div>
                  </button>
                ))}
                {campMetrics.length === 0 && campaigns.length === 0 && (
                  <p className="text-xs text-muted-foreground text-center py-4">{t('dashboard.noCampaigns')}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Noah Summary */}
          <Card className="glass-card overflow-hidden">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold">Noah</h3>
                  <p className="text-[10px] text-muted-foreground">{t('dashboard.noahSummary')}</p>
                </div>
                <div className="ml-auto flex items-center gap-2">
                  <NoahVoiceButton text={dailySummary?.noah_summary} testId="dashboard-noah-voice" />
                  <Badge variant="outline" className="text-emerald-700 dark:text-emerald-400 border-emerald-500 text-[10px]">{t('dashboard.live')}</Badge>
                </div>
              </div>
              <div className="p-3 bg-primary/10 rounded-xl border border-violet-500/20 max-h-40 overflow-y-auto">
                {dailySummary?.noah_summary ? (
                  <p className="text-xs leading-relaxed whitespace-pre-line">{dailySummary.noah_summary}</p>
                ) : (
                  <p className="text-xs text-muted-foreground">{t('dashboard.loadingNoah')}</p>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="p-2 bg-muted/30 rounded-lg text-center">
                  <p className="text-sm font-bold text-emerald-700 dark:text-emerald-400"><AnimatedNumber value={prodCount} /></p>
                  <p className="text-[10px] text-muted-foreground">Productos</p>
                </div>
                <div className="p-2 bg-muted/30 rounded-lg text-center">
                  <p className="text-sm font-bold text-brand-deep dark:text-brand-deep dark:text-brand-green"><AnimatedNumber value={totalChats} /></p>
                  <p className="text-[10px] text-muted-foreground">Chats</p>
                </div>
                <div className="p-2 bg-muted/30 rounded-lg text-center">
                  <p className="text-sm font-bold text-amber-700 dark:text-amber-400"><AnimatedNumber value={quotesTotal} /></p>
                  <p className="text-[10px] text-muted-foreground">Cotizaciones</p>
                </div>
              </div>
              <Button variant="outline" size="sm" className="w-full text-xs" onClick={() => navigate('/analytics')}>
                {t('dashboard.viewAnalytics')}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
